import {inject, Injectable, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {AccountService} from 'src/api';

export interface AppSubscriptionStatus {
  is_expired: boolean;
  status?: string | null;
  is_ending_soon?: boolean | null;
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionStatusService {
  private readonly accountService = inject(AccountService);
  private readonly _status = signal<AppSubscriptionStatus>({
    is_expired: false,
    status: null,
    is_ending_soon: null,
  });

  readonly status = this._status.asReadonly();
  private inflight: Promise<void> | null = null;

  async load(): Promise<void> {
    if (!this.inflight) {
      this.inflight = this.fetchAndSet();
    }
    await this.inflight;
  }

  /**
   * Force a fresh fetch (e.g. after subscription was created without a full page reload).
   * Waits for any in-flight load before starting so concurrent calls do not race on _status.
   */
  async reload(): Promise<void> {
    if (this.inflight) {
      await this.inflight.catch(() => undefined);
    }
    const fetch = this.fetchAndSet();
    this.inflight = fetch;
    await fetch;
  }

  private fetchAndSet(): Promise<void> {
    return this.runFetch().finally(() => {
      this.inflight = null;
    });
  }

  private async runFetch(): Promise<void> {
    try {
      const raw = await firstValueFrom(this.accountService.getSubscriptionStatus()) as AppSubscriptionStatus;
      this._status.set({
        is_expired: raw.is_expired ?? false,
        status: raw.status ?? null,
        is_ending_soon: raw.is_ending_soon ?? null,
      });
    } catch (error) {
      console.error('Error checking subscription status', error);
      this._status.set({
        is_expired: false,
        status: null,
        is_ending_soon: null,
      });
    }
  }
}
